/**
 * Final frontend-owned wiring for Level-3 automation installs.
 *
 * ZIP import can copy and remap Y.js docs, but the concrete workflow is created
 * only afterwards by `complete-install`. This helper writes that returned
 * workflow id into imported automation blocks without running the workflow
 * selector cleanup path, so template-provided mappings stay intact.
 */
import type {
  BlockSnapshot,
  Slice,
  SliceSnapshot,
  Store,
} from '@blocksuite/affine/store';

import {
  type AutomationDatabaseBindings,
  type AutomationDatabaseSurfaceBinding,
  buildColumnMappings,
} from './automation-database-bindings';

type BlockLike = {
  readonly model: {
    readonly id: string;
    readonly flavour?: string;
    readonly props: Record<string, unknown>;
  };
};

export interface WireInstalledAutomationBlocksOptions {
  readonly workflowId: string;
  readonly docIdReplacements?: ReadonlyMap<string, string>;
  readonly databaseBindings?: AutomationDatabaseBindings;
  readonly automationBlockIds?: readonly string[] | ReadonlySet<string>;
}

export interface InstalledAutomationArtifactBinding {
  readonly sourceBlockId?: string;
  readonly targetDocId?: string;
  readonly targetBlockId?: string;
  readonly workflowId?: string;
}

export function wireInstalledAutomationArtifactBindings(
  docs: readonly Store[],
  bindings: readonly InstalledAutomationArtifactBinding[]
): void {
  const seenTargets = new Set<string>();
  const mutations: Array<{
    doc: Store;
    props: Record<string, unknown>;
    workflowId: string;
  }> = [];
  for (const binding of bindings) {
    if (!binding.targetDocId || !binding.targetBlockId || !binding.workflowId) {
      throw new Error('Composite automation binding is incomplete');
    }
    const targetKey = `${binding.targetDocId}:${binding.targetBlockId}`;
    if (seenTargets.has(targetKey)) {
      throw new Error(
        `Composite automation target is duplicated: ${targetKey}`
      );
    }
    seenTargets.add(targetKey);
    const doc = docs.find(candidate => candidate.id === binding.targetDocId);
    const block = doc?.getBlock(binding.targetBlockId);
    if (!doc || block?.model.flavour !== 'affine:automation') {
      throw new Error(
        `Composite automation target was not imported: ${targetKey}`
      );
    }
    const props = block.model.props as Record<string, unknown>;
    const currentWorkflowId = props.workflowId;
    if (currentWorkflowId === binding.workflowId) continue;
    if (
      typeof currentWorkflowId === 'string' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(
        currentWorkflowId
      )
    ) {
      throw new Error(
        `Composite automation target already points to a different concrete workflow: ${targetKey}`
      );
    }
    mutations.push({ doc, props, workflowId: binding.workflowId });
  }
  for (const mutation of mutations) {
    mutation.doc.transact(() => {
      mutation.props.workflowId = mutation.workflowId;
    });
  }
}

export function wireInstalledAutomationBlocks(
  docs: readonly Store[],
  options: WireInstalledAutomationBlocksOptions
): void {
  if (!options.workflowId) return;

  const automationBlockIds = normalizeAutomationBlockIds(
    options.automationBlockIds
  );
  for (const doc of docs) {
    const automationBlocks = getBlocksByFlavour(
      doc,
      'affine:automation'
    ).filter(block =>
      automationBlockIds ? automationBlockIds.has(block.model.id) : true
    );
    if (automationBlocks.length === 0) continue;

    doc.transact(() => {
      for (const block of automationBlocks) {
        block.model.props.workflowId = options.workflowId;
        rewriteAutomationDocReferences(
          block.model.props,
          options.docIdReplacements
        );
        applyAutomationDatabaseBindings(
          block.model.props,
          options.databaseBindings
        );
      }
    });
  }
}

export function getAutomationBlockIds(doc: Store): string[] {
  return getBlocksByFlavour(doc, 'affine:automation').map(
    block => block.model.id
  );
}

export function getAutomationBlockIdsFromSliceSnapshot(
  snapshot: Pick<SliceSnapshot, 'content'>
): string[] {
  const ids: string[] = [];
  for (const block of snapshot.content) {
    collectAutomationBlockIdsFromSnapshot(block, ids);
  }
  return ids;
}

export function getAutomationBlockIdsFromSlice(
  slice: Pick<Slice, 'content'>
): string[] {
  const ids: string[] = [];
  for (const block of slice.content) {
    collectAutomationBlockIdsFromSliceBlock(block, ids);
  }
  return ids;
}

function collectAutomationBlockIdsFromSnapshot(
  block: BlockSnapshot,
  ids: string[]
): void {
  if (block.flavour === 'affine:automation') {
    ids.push(block.id);
  }
  for (const child of block.children ?? []) {
    collectAutomationBlockIdsFromSnapshot(child, ids);
  }
}

function collectAutomationBlockIdsFromSliceBlock(
  block: Slice['content'][number],
  ids: string[]
): void {
  if (block.flavour === 'affine:automation') {
    ids.push(block.id);
  }
  for (const child of block.children ?? []) {
    collectAutomationBlockIdsFromSliceBlock(child, ids);
  }
}

function normalizeAutomationBlockIds(
  automationBlockIds: readonly string[] | ReadonlySet<string> | undefined
): ReadonlySet<string> | null {
  if (!automationBlockIds) return null;
  return automationBlockIds instanceof Set
    ? automationBlockIds
    : new Set(automationBlockIds);
}

function applyAutomationDatabaseBindings(
  props: Record<string, unknown>,
  bindings: AutomationDatabaseBindings | undefined
): void {
  if (!bindings) return;

  const columnMappings = buildColumnMappings(bindings);
  remapInputMappings(props.inputMappings, columnMappings);
  remapOutputMappings(props.outputMappings, columnMappings);
  remapMatchConfigColumns(props.matchConfigs, columnMappings);
  remapColumnProperty(props.statusTracking, 'columnId', columnMappings);
  remapStringArray(props.selectedColumns, columnMappings);

  for (const surface of bindings.surfaces) {
    if (surface.mode !== 'use_existing') continue;
    if (surface.databaseRole === 'input') {
      applyInputSurfaceBinding(props, surface);
    } else if (surface.databaseRole === 'output') {
      applyOutputSurfaceBinding(props, surface);
    }
  }
}

function applyInputSurfaceBinding(
  props: Record<string, unknown>,
  surface: AutomationDatabaseSurfaceBinding
): void {
  const tableId = surface.targetDatabaseBlockId;
  if (!tableId) return;
  props.sourceTableId = tableId;
  props.sourceTableName = surface.targetDatabaseName ?? surface.databaseLabel;
  props.sourceDocId = surface.targetDocId ?? props.sourceDocId;

  const tables = Array.isArray(props.inputTables)
    ? (props.inputTables as Array<Record<string, unknown>>)
    : [];
  const index = Math.max(0, surface.databaseIndex);
  const current = tables[index] ?? {};
  tables[index] = {
    ...current,
    tableId,
    tableName: surface.targetDatabaseName ?? surface.databaseLabel,
    docId: surface.targetDocId,
    role: current.role ?? (index === 0 ? 'lead' : 'secondary'),
  };
  props.inputTables = tables;
}

function applyOutputSurfaceBinding(
  props: Record<string, unknown>,
  surface: AutomationDatabaseSurfaceBinding
): void {
  const tableId = surface.targetDatabaseBlockId;
  if (!tableId) return;
  const tables = Array.isArray(props.outputTables)
    ? (props.outputTables as Array<Record<string, unknown>>)
    : [];
  const index = Math.max(0, surface.databaseIndex);
  const previousTableId = readString(tables[index]?.tableId);
  tables[index] = {
    ...tables[index],
    tableId,
    tableName: surface.targetDatabaseName ?? surface.databaseLabel,
    docId: surface.targetDocId,
  };
  props.outputTables = tables;

  if (previousTableId && previousTableId !== tableId) {
    remapOutputTableIds(props.outputMappings, previousTableId, tableId);
    remapMatchConfigTableIds(props.matchConfigs, previousTableId, tableId);
  }
}

function rewriteAutomationDocReferences(
  props: Record<string, unknown>,
  docIdReplacements: ReadonlyMap<string, string> | undefined
): void {
  if (!docIdReplacements || docIdReplacements.size === 0) return;

  props.sourceDocId = remapDocId(props.sourceDocId, docIdReplacements);
  remapTableRefs(props.inputTables, docIdReplacements);
  remapTableRefs(props.outputTables, docIdReplacements);
}

function remapTableRefs(
  value: unknown,
  docIdReplacements: ReadonlyMap<string, string>
): void {
  if (!Array.isArray(value)) return;

  for (const tableRef of value) {
    if (!isRecord(tableRef)) continue;
    tableRef.docId = remapDocId(tableRef.docId, docIdReplacements);
  }
}

function remapDocId(
  value: unknown,
  docIdReplacements: ReadonlyMap<string, string>
): unknown {
  if (typeof value !== 'string') return value;
  return docIdReplacements.get(value) ?? value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function remapInputMappings(
  value: unknown,
  columnMappings: Readonly<Record<string, string>>
): void {
  if (!Array.isArray(value)) return;
  for (const mapping of value) {
    remapColumnProperty(mapping, 'columnId', columnMappings);
  }
}

function remapOutputMappings(
  value: unknown,
  columnMappings: Readonly<Record<string, string>>
): void {
  if (!Array.isArray(value)) return;
  for (const mapping of value) {
    remapColumnProperty(mapping, 'targetColumnId', columnMappings);
    remapColumnProperty(mapping, 'columnId', columnMappings);
  }
}

function remapMatchConfigColumns(
  value: unknown,
  columnMappings: Readonly<Record<string, string>>
): void {
  if (!Array.isArray(value)) return;
  for (const config of value) {
    if (!isRecord(config) || !Array.isArray(config.keys)) continue;
    for (const key of config.keys) {
      remapColumnProperty(key, 'columnId', columnMappings);
    }
  }
}

function remapColumnProperty(
  value: unknown,
  key: string,
  columnMappings: Readonly<Record<string, string>>
): void {
  if (!isRecord(value)) return;
  const current = value[key];
  if (typeof current !== 'string') return;
  value[key] = columnMappings[current] ?? current;
}

function remapStringArray(
  value: unknown,
  columnMappings: Readonly<Record<string, string>>
): void {
  if (!Array.isArray(value)) return;
  for (let index = 0; index < value.length; index++) {
    const current = value[index];
    if (typeof current !== 'string') continue;
    value[index] = columnMappings[current] ?? current;
  }
}

function remapOutputTableIds(
  value: unknown,
  fromTableId: string,
  toTableId: string
): void {
  if (!Array.isArray(value)) return;
  for (const mapping of value) {
    if (!isRecord(mapping)) continue;
    if (mapping.tableId === fromTableId) mapping.tableId = toTableId;
  }
}

function remapMatchConfigTableIds(
  value: unknown,
  fromTableId: string,
  toTableId: string
): void {
  if (!Array.isArray(value)) return;
  for (const config of value) {
    if (!isRecord(config)) continue;
    if (config.targetTableId === fromTableId) config.targetTableId = toTableId;
  }
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function getBlocksByFlavour(doc: Store, flavour: string): BlockLike[] {
  return doc.getBlocksByFlavour(flavour) as unknown as BlockLike[];
}
